/**
 * How a list of actions is carved into groups.
 *
 * The board and the grouped list are the same question asked twice — "put these
 * tasks in piles" — so the piles are computed once, here, in plain data. Which
 * pile an action belongs in is a judgement (what counts as "this week"?), and
 * judgements belong somewhere testable rather than inside a render.
 *
 * ICS section grouping is deliberately absent: section resolution walks
 * `ics_position.parent_id` on the server and the browser has no
 * position-to-section map to do it with.
 */

import dayjs from 'dayjs';
import {
  ACTION_PRIORITY_VALUES,
  ACTION_STATUS_VALUES,
  UNASSIGNED_LABEL,
  actionPriorityRank,
  isActionOverdue,
  resolveActionAssignee,
  type AssigneeDirectory,
  EMPTY_ASSIGNEE_DIRECTORY,
} from './actionEditModel';

export const ACTION_GROUPINGS = [
  'status',
  'assignee',
  'priority',
  'due',
] as const;

export type ActionGrouping = (typeof ACTION_GROUPINGS)[number];

export const ACTION_GROUPING_LABELS: Record<ActionGrouping, string> = {
  status: 'Status',
  assignee: 'Assignee',
  priority: 'Priority',
  due: 'Due',
};

export type GroupableAction = {
  id?: string | number;
  name?: string | null;
  status?: string | null;
  priority?: string | null;
  target_date?: Date | string | null;
  assignee_kind?: string | null;
  assignee_id?: string | null;
  poc?: string | null;
  poc_user_name?: string | null;
};

export type ActionGroup<T> = {
  /** Stable identity, also the value a drag onto this group would write. */
  id: string;
  title: string;
  actions: T[];
};

/**
 * Due-date buckets, in the order someone triaging reads them.
 *
 * "Overdue" comes first because it is the only one that is already a problem.
 * "Earlier" holds work whose date has passed without it being a problem — a
 * closed task, however late it finished — and sits after the upcoming windows
 * because nothing there needs a decision. "No due date" is last: it is the
 * absence of an answer rather than a late one.
 */
export const DUE_WINDOWS = [
  'overdue',
  'today',
  'tomorrow',
  'this-week',
  'later',
  'earlier',
  'none',
] as const;

export type DueWindow = (typeof DUE_WINDOWS)[number];

export const DUE_WINDOW_LABELS: Record<DueWindow, string> = {
  overdue: 'Overdue',
  today: 'Today',
  tomorrow: 'Tomorrow',
  'this-week': 'This week',
  later: 'Later',
  earlier: 'Earlier',
  none: 'No due date',
};

/**
 * Which bucket a task's due date falls in.
 *
 * "This week" means within the next seven days rather than to the end of the
 * calendar week, because an operational period does not reset on Sunday.
 */
export function dueWindowFor(
  action: Pick<GroupableAction, 'status' | 'target_date'>,
  now: Date = new Date()
): DueWindow {
  if (!action.target_date) return 'none';
  const target = dayjs(action.target_date);
  if (!target.isValid()) return 'none';

  if (isActionOverdue(action.status, action.target_date, now)) return 'overdue';

  const today = dayjs(now).startOf('day');
  const days = target.startOf('day').diff(today, 'day');
  if (days < 0) return 'earlier';
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days <= 7) return 'this-week';
  return 'later';
}

/**
 * The groups for a grouping, including the empty ones.
 *
 * Empty columns are kept on purpose for the fixed vocabularies — status,
 * priority and due window — because a board with no "Closed" column looks
 * broken and gives drag nowhere to land. Assignee has no fixed vocabulary, so
 * only the people who actually have work appear.
 */
export function groupActions<T extends GroupableAction>(
  actions: T[],
  grouping: ActionGrouping,
  options: { directory?: AssigneeDirectory; now?: Date } = {}
): ActionGroup<T>[] {
  const { directory = EMPTY_ASSIGNEE_DIRECTORY, now = new Date() } = options;

  if (grouping === 'assignee') {
    const groups = new Map<string, ActionGroup<T>>();
    actions.forEach((action) => {
      const resolved = resolveActionAssignee(action, directory);
      const id = resolved.id ?? '__unassigned';
      const existing = groups.get(id);
      if (existing) {
        existing.actions.push(action);
        return;
      }
      groups.set(id, { id, title: resolved.label, actions: [action] });
    });

    // Named assignees alphabetically, Unassigned last: it is a gap to close,
    // not a person, and it belongs where the eye stops.
    return [...groups.values()].sort((a, b) => {
      if (a.id === '__unassigned') return 1;
      if (b.id === '__unassigned') return -1;
      return a.title.localeCompare(b.title);
    });
  }

  const buckets: { id: string; title: string }[] =
    grouping === 'status'
      ? ACTION_STATUS_VALUES.map((value) => ({ id: value, title: value }))
      : grouping === 'priority'
        ? ACTION_PRIORITY_VALUES.map((value) => ({ id: value, title: value }))
        : DUE_WINDOWS.map((value) => ({
            id: value,
            title: DUE_WINDOW_LABELS[value],
          }));

  const keyFor = (action: T): string =>
    grouping === 'status'
      ? (action.status ?? '')
      : grouping === 'priority'
        ? (action.priority ?? 'Normal')
        : dueWindowFor(action, now);

  const byKey = new Map<string, T[]>(buckets.map(({ id }) => [id, []]));
  const unrecognised: T[] = [];
  actions.forEach((action) => {
    const bucket = byKey.get(keyFor(action));
    if (bucket) bucket.push(action);
    else unrecognised.push(action);
  });

  const groups: ActionGroup<T>[] = buckets.map(({ id, title }) => ({
    id,
    title,
    actions: byKey.get(id) ?? [],
  }));

  // Never silently drop a row whose value is not in the vocabulary; a CHECK
  // constraint changing on the server should show up as a visible column.
  if (unrecognised.length > 0) {
    groups.push({ id: '__other', title: 'Other', actions: unrecognised });
  }

  return groups;
}

/**
 * Sort within a group: most urgent priority, then soonest due, then name.
 *
 * Missing due dates sort last rather than first, since an unscheduled task is
 * less pressing than a scheduled one, not more.
 */
export function sortActionsForBoard<
  T extends GroupableAction & { name?: string | null },
>(actions: T[]): T[] {
  return [...actions].sort((a, b) => {
    const priority =
      actionPriorityRank(a.priority) - actionPriorityRank(b.priority);
    if (priority !== 0) return priority;

    const aDue = a.target_date ? dayjs(a.target_date).valueOf() : null;
    const bDue = b.target_date ? dayjs(b.target_date).valueOf() : null;
    if (aDue !== bDue) {
      if (aDue == null) return 1;
      if (bDue == null) return -1;
      return aDue - bDue;
    }

    return (a.name ?? '').localeCompare(b.name ?? '');
  });
}

/**
 * Whether dragging between these groups is a change the board can make.
 *
 * Status and priority are single columns on the action, so a drag is one write.
 * Due window is a range of dates with no single obvious value to pick, and an
 * assignee column would have to invent an id, so both are read-only groupings.
 */
export function groupingIsDraggable(grouping: ActionGrouping): boolean {
  return grouping === 'status' || grouping === 'priority';
}

/** The patch a drag from one group to another should write, if any. */
export function groupDropPatch(
  grouping: ActionGrouping,
  groupId: string
): Record<string, unknown> | null {
  if (groupId.startsWith('__')) return null;
  if (grouping === 'status') return { status: groupId };
  if (grouping === 'priority') return { priority: groupId };
  return null;
}

/** "3 overdue" style counts for a header, empty groups omitted. */
export function summarizeGroups<T>(groups: ActionGroup<T>[]): string {
  const total = groups.reduce((sum, group) => sum + group.actions.length, 0);
  return `${total} ${total === 1 ? 'task' : 'tasks'}`;
}

export { UNASSIGNED_LABEL };
